<template>
  <v-card width="100%">
    <div class="security-card-content two-factor-card-content">
      <div class="step-back-container" v-if="requestPassword">
        <v-icon color="#333333" size="22" @click="$emit('goBackStep')">
          mdi-arrow-left
        </v-icon>
      </div>

      <h2 class="two-factor-title">
        {{ $t("twoFactor.title") }}
        <strong>
          {{ customerPhone }}
        </strong>
      </h2>

      <div class="remaining-time-countdown" v-if="remainingTime">
        {{ $t("twoFactor.codeExpiration", { expirationTime: remainingTime }) }}
      </div>

      <div @click="resendCode()" class="resend-code" v-if="remainingTime === 0">
        {{ $t("twoFactor.resendCode") }}
      </div>

      <v-scale-transition>
        <div class="toast-container" v-if="pillErrorMessage && displayError">
          <ToastNotification :toastText="pillErrorMessage" />
        </div>
      </v-scale-transition>

      <div class="input-box-container">
        <input
          type="number"
          v-model="authCode[n - 1]"
          maxlength="1"
          oninput="this.value=this.value.replace(/[^0-9]/g,'');"
          onKeyPress="if(this.value.length==1) return false;"
          v-for="n in 6"
          @input="tabNextField(n + 1, $event)"
          :key="n"
          :id="`input-${n}`"
        />
      </div>
      <div class="confirm-btn">
        <NewButton
          :text="$t('twoFactor.confirm')"
          :loading="loading"
          :disabled="nextDisabled"
          @on-click="onConfirmClick()"
        />
      </div>

      <div class="support-btn">
        <NewButton
          :text="$t('base.help')"
          @on-click="$intercom.showNewMessage($t('twoFactor.intercom'))"
        />
      </div>

      <div class="cancel-text" @click="onDeclineClick()">{{ declineText }}</div>
    </div>
  </v-card>
</template>

<script>
import { mapGetters } from "vuex";
import { twoFactorFunctions } from "@/mixins/twoFactorMixin.js";
import NewButton from "@/components/shared/NewButton.vue";
import ToastNotification from "@/components/shared/ToastNotification.vue";

export default {
  name: "TwoFactor",

  data: () => ({
    interval: {},
    authCode: [],
    twoFactorRequestError: "",
    remainingTime: null,
    displayError: false,
  }),

  components: { ToastNotification, NewButton },

  mixins: [twoFactorFunctions],

  props: {
    loading: {
      type: Boolean,
      default: false,
    },

    authenticationErrors: {
      type: Array,
      default: () => [],
    },

    declineText: {
      type: String,
      default: "",
    },

    requestPassword: {
      type: Boolean,
      default: false,
    },
  },

  watch: {
    pillErrorMessage: {
      async handler(currentValue) {
        if (currentValue) {
          await new Promise(resolve => setTimeout(resolve, 3000));
          this.displayError = false;
        }
      },
    },
  },

  computed: {
    ...mapGetters(["profile", "customerPhone"]),

    nextDisabled() {
      return (
        this.authCode.filter(number => number !== "").length < 6 ||
        this.remainingTime <= 0
      );
    },

    pillErrorMessage() {
      return (
        this.twoFactorRequestError ||
        this.getAuthenticationErrorMessage(this.authenticationErrors)
      );
    },
  },

  methods: {
    resendCode() {
      this.error = false;
      this.authCode = [];
      this.request2Factor();
    },
    /**
     * Auto tab when the user insert a digit
     */
    tabNextField(nextIndex, event) {
      if (["deleteContentBackward"].includes(event.inputType)) return;
      const input = document.getElementById(`input-${nextIndex}`);
      // if we are in the last input box, the element will be null.
      if (input === null) return;
      input.focus();
    },

    async request2Factor() {
      // Expected Float (timeout)
      const expirationTime = await this.request2FA();

      // If object, it's an error
      if (typeof expirationTime === "object") {
        const errorCode = expirationTime.status;
        this.twoFactorRequestError = this.$t(`twoFactor.unsentCodeErrors.${errorCode}`);
        this.displayError = true;
      } else {
        this.remainingTime = Math.ceil(Number(expirationTime));
        this.interval = setInterval(() => {
          if (this.remainingTime <= 0) {
            return;
          }
          this.remainingTime -= 1;
        }, 1000);
      }
    },

    onConfirmClick() {
      this.$emit("setTFAAuthorization", this.authCode.join(""));
      this.$emit("afterConfirmAction");
    },

    onDeclineClick() {
      this.authCode.splice(0, this.authCode.length);
      this.$emit("setTFAAuthorization", "");
      // Parent component could have or not the following event, if the parent component
      // needs further actions after click on decline, it should be specified
      this.$emit("afterOnDecline");
    },

    isValidErrorArray(errors) {
      return (
        Array.isArray(errors) &&
        errors.some(
          error => typeof error === "object" && Object.keys(error).includes("message"),
        )
      );
    },

    getAuthenticationErrorMessage(errors) {
      if (!this.isValidErrorArray(errors)) return this.$t("twoFactor.genericError");

      this.displayError = true;

      const errorMapping = {
        "2FA VERIFICATION FAILED": this.$t("twoFactor.codeError"),
        "PASSWORD VERIFICATION FAILED": this.$t("twoFactor.passwordError"),
      };

      // Extract the message, then remove periods and applying uppercase
      const errorMessages = errors.map(e => e.message.replace(/\./g, "").toUpperCase().trim());

      // Match the API message with the errorMapping object to get the respective key
      const matchingMessageKey = Object.keys(errorMapping).find(key => errorMessages.some(message => message.includes(key)));

      // If an error is found, its message is returned, otherwise a generic message is displayed.
      return matchingMessageKey ?
        errorMapping[matchingMessageKey] :
        this.$t("twoFactor.genericError");
    },
  },

  beforeDestroy() {
    clearInterval(this.interval);
  },

  async mounted() {
    await this.request2Factor();
  },
};
</script>

<style lang="scss" scoped>
/** Disabling spin button */

/* Chrome, Safari, Edge, Opera */
input::-webkit-outer-spin-button,
input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

/* Firefox */
input[type="number"] {
  -moz-appearance: textfield;
}
.two-factor-title {
  font-weight: 500;
  font-size: 22px;
  line-height: 160%;
  color: #333333;
  margin-bottom: 20px;

  @media (max-width: 380px) {
    font-size: 20px;
    line-height: 140%;
  }
}

.title-container {
  max-width: 270px;
  text-align: center;
}

.input-box-container {
  display: flex;
  justify-content: space-between;
  margin-bottom: 40px;
}

.cancel {
  font-weight: 500;
  font-size: 14px;
  line-height: 17px;
  display: flex;
  align-items: center;
  color: #828282;
}

.cancel:hover {
  cursor: pointer;
}

.resend-code {
  font-weight: 500;
  font-size: 14px;
  line-height: 140%;
  color: #6d48ff;
  margin-bottom: 40px;
}
.resend-code:hover {
  cursor: pointer;
}

/* Firefox */
input[type="number"] {
  -moz-appearance: textfield;
}
input {
  text-align: center;
  font-weight: 500;
  font-size: 18px;
  letter-spacing: 0.05em;

  background: #ffffff;
  /* Grises/Gris 04 */
  max-width: 60px;
  height: 60px;
  border: 1px solid #bdbdbd;
  box-sizing: border-box;
  border-radius: 5px;

  @media (max-width: 600px) {
    // the six boxes will cover the dialog width
    // we subtract 10px for the gap between the boxes
    width: calc((100% / 6) - 10px);
  }
}
input:focus {
  outline: none;
}

.support:hover {
  cursor: pointer;
}

.support {
  font-weight: 500;
  font-size: 14px;
  line-height: 17px;

  color: #6d48ff;
}

.confirm-btn {
  margin-bottom: 40px;
}

.support-btn::v-deep .new-btn {
  background-color: #ffffff !important;
  border: 1px solid #000000 !important;
  span {
    color: #000000 !important;
  }
}

.remaining-time-countdown {
  font-weight: 500;
  font-size: 16px;
  line-height: 160%;
  color: #646363;
  margin-bottom: 40px;
}

.toast-container {
  display: flex;
  justify-content: center;
  margin: 40px 0;
}
</style>
